import * as React from 'react';
// Import hooks.
import {
	useInputAddressesSelectionList,
	UseInputAddressesSelectionListResults,
} from '@app/hooks/view/input';
// Import props.
import {
	FormPopupProps,
	ScrollbarProps,
	FormPopupAddressVerificationProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
	PopupWidth,
} from '@app/enumerations';
// Import api models.
import {
	Address,
} from '@app/models/api';

interface UseFormPopupAddressVerificationParameters {
	id: string;
	props: FormPopupAddressVerificationProps,
	showValidations?: boolean;
}

interface UseFormPopupAddressVerificationResults {
	submitForm: (selectedAddress: Address) => void;
	addressesSelectionListInput: UseInputAddressesSelectionListResults;
	applyFormProps: () => FormPopupProps & ScrollbarProps;
}

const useFormPopupAddressVerification = (params: UseFormPopupAddressVerificationParameters): UseFormPopupAddressVerificationResults => {

	// #region exported methods

	/**
	 *  Starts the submission process for the address.
	 **/
	const submitForm = (selectedAddress: Address): void => {
		params.props.onSaveAddress(selectedAddress);		
	}

	// #endregion exported methods


	// #region private methods

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): FormPopupProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
			title: 'Address Verification',
			themeType: AppThemeColor.PRIMARY,
			fixedNavigation: false,
			isOpen: params.props.isOpen,
			popupWidth: PopupWidth.MEDIUM,
			onCloseClick: params.props.onClose,
			keepMounted: true,
		};
	};

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	const generateIdForAddressesSelectionListInput = (): string => {
		return `${generateIdForForm()}-addressSelection`;
	}

	// #endregion private methods


	// #region variables

	// Setup the address 1 input hook.
	const addressesSelectionListInput: UseInputAddressesSelectionListResults = useInputAddressesSelectionList({
		id: generateIdForAddressesSelectionListInput(),
		label: 'We were unable to find a matching address.',
		required: true,
		disabled: false,
		address1: params.props.verifiedAddress,
		address1ButtonLabel: 'Use this address',
		address2: params.props.enteredAddress,
		address2ButtonLabel: 'Use the address I entered',
		selectedAddress: undefined,
		onSelectAddress: submitForm,
		showValidation: params.showValidations,
	});

	// #endregion private variables


	// #region life-cycle methods.

	// Called when the hook is initially mounted, and returns a call back that will be called on unmounting.
	React.useEffect(() => {
		// Initially the first input should be focused.
		addressesSelectionListInput.focusToInput();
	}, []);

	// #endregion life-cycle methods.


	return {
		submitForm: submitForm,
		addressesSelectionListInput: addressesSelectionListInput,
		applyFormProps: applyFormProps,
	};
}

// Export parameters.
export {
	UseFormPopupAddressVerificationParameters,
}

// Export results.
export {
	UseFormPopupAddressVerificationResults,
}

export default useFormPopupAddressVerification;